import { useJsApiLoader } from '@react-google-maps/api';
import type { Addresses } from 'api/types/addresses.types';
import { MarkerBlueIcon } from 'assets/icons/app';
import { useCheckCoverageAddress } from 'pages/queries/address.queries';
import React, { useCallback, useEffect } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { use100vh } from 'react-div-100vh';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { lazyWithPreload } from 'utils/bundle';
import { useBasePath } from 'utils/router/hooks/useBasePath';
import { notifyErrorObject } from 'wrappers/reporting';

import styles from '../styles.module.scss';
import MapFooterContainer from './components/MapFooterContainer';

const GoogleMap = lazyWithPreload(() => import('@react-google-maps/api').then((module) => ({ default: module.GoogleMap })));
GoogleMap.preload();

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: '0px',
  left: '0px',
};

const center = {
  lat: 25.2048,
  lng: 55.2708,
};

const MapsParentContainer = styled.div`
  height: 100vh !important;
  width: 100% !important;
  flex: 1;
`;

const StyledMarkerBlueIcon = styled(MarkerBlueIcon)`
  position: absolute;
  top: 47.5%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

interface LocationState {
  address: Addresses.Address;
  goBack?: boolean;
  lat: string;
  lng: string;
}

export const AddressGeofence: React.FC = () => {
  const location = useLocation();
  const { address, lat, lng, goBack } = location.state as LocationState;
  const defaultLocation = { lat: parseFloat(lat), lng: parseFloat(lng) };
  const basePath = useBasePath();
  const [currentPosition, setCurrentPosition] = React.useState<{ lat: number; lng: number }>({
    lat: defaultLocation.lat,
    lng: defaultLocation.lng,
  });
  const navigate = useNavigate();
  const mapInstance = React.useRef<any>(null);
  const [isAddressValid, setIsAddressValid] = React.useState(false);
  const [geofence, setGeofence] = React.useState<Addresses.Geofence>();
  const [markerPos, setMarkerPos] = React.useState<{ lat: number; lng: number } | undefined>({
    lat: defaultLocation.lat,
    lng: defaultLocation.lng,
  });
  const [placeId, setPlaceId] = React.useState<string>();
  const height = use100vh();

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'maps',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || '',
  });

  useEffect(() => {
    if (loadError) {
      notifyErrorObject(loadError);
    }
  }, [loadError]);

  const { mutateAsync: validateAddress, status } = useCheckCoverageAddress({
    onSuccess: (data) => {
      setGeofence(data.geofence);
      setIsAddressValid(true);
    },
    onError: () => {
      setIsAddressValid(false);
    },
  });

  const checkCoverage = useCallback(
    async (pos: { lat: number; lng: number }) => {
      if (!pos) return;
      await validateAddress(pos);
    },
    [validateAddress]
  );

  const onDragEndHandler = React.useCallback(() => {
    setPlaceId(undefined);
    if (!markerPos) return;
    checkCoverage(markerPos);
  }, [checkCoverage, markerPos]);

  const getCurrentLocation = async () =>
    new Promise<GeolocationPosition>((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          resolve(position);
        });
      } else {
        reject(new Error('Geolocation is not supported by this browser.'));
      }
    });

  const handleMapLoad = (map: any) => {
    // store map in state
    mapInstance.current = map;

    // if a default address is provided
    if (defaultLocation) {
      map.setCenter(defaultLocation);
      setMarkerPos(defaultLocation);
      checkCoverage(defaultLocation);
    }

    /**
     * we will get location but if default address then not replace it,
     * and in case user is far from location
     * we will show message to user for that
     */
    // init user current location
    getCurrentLocation()
      .then((position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        // in case address is available
        // don't set current location as default
        if (!defaultLocation) {
          map.setCenter(pos);
          setMarkerPos(pos);
          checkCoverage(pos);
        }

        setCurrentPosition(pos);
      })
      .catch(() => {
        // handle error
      });
  };

  const onMoveToCurrentPosition = useCallback(() => {
    getCurrentLocation()
      .then((position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        mapInstance.current.setCenter(pos);
        setMarkerPos(pos);
        checkCoverage(pos);
        setCurrentPosition(pos);
      })
      .catch(() => {
        // handle error
      });
  }, []);

  // Map click event handler
  const onDragHandler = useCallback(() => {
    setMarkerPos(mapInstance.current?.getCenter().toJSON());
  }, []);

  const handleConfirmPin = () => {
    navigate(`${basePath}/addressType?step=${1}`, {
      state: {
        ...geofence,
        ...markerPos,
        placeId,
        goBack,
        address,
      },
    });
  };

  return (
    <MapsParentContainer>
      <Row>
        <Col className="p-0" style={{ height: height || '100%' }}>
          {isLoaded && (
            <GoogleMap
              options={{
                disableDefaultUI: true,
                draggable: true,
                gestureHandling: 'greedy',
                zoomControl: false,
                maxZoom: 20,
                minZoom: 20,
              }}
              mapContainerStyle={containerStyle}
              center={center}
              zoom={20}
              mapContainerClassName={styles.mapsParentContainer}
              onLoad={handleMapLoad}
              onDragEnd={onDragEndHandler}
              onDrag={onDragHandler}
            >
              {markerPos && <StyledMarkerBlueIcon />}
            </GoogleMap>
          )}
        </Col>
      </Row>

      <MapFooterContainer
        currentPosition={currentPosition}
        mapCenter={center}
        status={status}
        onPressConfirm={handleConfirmPin}
        isAddressValid={isAddressValid}
        onMoveToCurrentPosition={onMoveToCurrentPosition}
      />
    </MapsParentContainer>
  );
};
